import {defer, from, of, zip} from 'rxjs';
import {ofType} from 'redux-observable';
import {catchError, mergeMap, retryWhen, tap} from 'rxjs/operators';

import {combineEpics} from 'web-app/util/redux-observable';
import * as actionTypes from 'web-app/react/constants/action-types';
import {FileUploadSubject} from 'web-app/util/subscriptions';
import * as SentryService from 'web-app/services/sentry';
import * as AccountSelectors from 'web-app/react/session/account/selectors';
import {genericRetryStrategy} from 'web-app/util/rxjs';

import * as Api from './api';
import * as Actions from './actions';
import * as NewActions from './new-actions';
import * as Helpers from './typed-helpers';
import * as TypedAPI from './typed-api';

const uploadFile = (action$, state$) => {
    return action$.pipe(
        ofType(actionTypes.FILE_UPLOAD_ADD_FILE),
        mergeMap(
            ({payload}) => {
                const onFileProgress = progress => FileUploadSubject.next({file: payload.file, progress});
                /**
                 * We're wrapping the zip operator in a defer so we can catch any errors that happen and pipe them to a catchError operator
                 * Without the defer operator errors caused by accessing undefined fields in state, e.g. `state.value.undefined.foo`
                 * would throw outside the pipe and cause the epic to stop working.
                 */
                return defer(() =>
                    zip(
                        from(
                            Helpers.compressFileIfImage(
                                payload.file,
                                AccountSelectors.imageScalingConfig(state$.value),
                            ),
                        ),
                        of({
                            forceUploadFallback: getForceUploadFallback(state$.value),
                            compressionConfig: AccountSelectors.imageScalingConfig(state$.value),
                            formName: payload.formName,
                            config: payload.config,
                            uncompressedFile: payload.file,
                        }),
                    ),
                ).pipe(
                    mergeMap(([file, {formName, config, uncompressedFile, compressionConfig, forceUploadFallback}]) =>
                        // Now begin uploading the file
                        TypedAPI.uploadFileObservable(file, forceUploadFallback, onFileProgress).pipe(
                            mergeMap(downloadURL =>
                                of(
                                    Actions.fileUploadSuccess(
                                        formName,
                                        file,
                                        downloadURL,
                                        config,
                                        uncompressedFile,
                                        compressionConfig,
                                    ),
                                ),
                            ),
                            retryWhen(
                                genericRetryStrategy({
                                    maxRetryAttempts: 3,
                                    scalingDuration: 100,
                                }),
                            ),
                            catchError(error =>
                                // If a single upload inside the concurrency fails we map that to an action
                                fileErrorObservable(formName, file, uncompressedFile, compressionConfig, error),
                            ),
                        ),
                    ),
                    catchError(error => {
                        /**
                         * Errors caught here are a result of something in the defer(() => zip) operator failing.
                         * We have to stub some of the values sent to the error handling action
                         */
                        return fileErrorObservable(
                            payload.formName,
                            payload.file,
                            payload.file,
                            AccountSelectors.fallbackImageCompressionConfig,
                            error,
                        );
                    }),
                );
            },
            // Maximum allow 6 concurrent file uploads
            6,
        ),
    );
};

const getForceUploadFallback = state => {
    // It doesn't feel safe to access such a deeply nested part of the state without any error handling,
    // so I'm wrapping it in a try/catch just to be safe!
    try {
        return state.session.account.features.indexOf('FALLBACK_S3_UPLOAD') >= 0;
    } catch {
        return false;
    }
};

const fileErrorObservable = (formName, file, uncompressedFile, compressionConfig, error) => {
    return of(Actions.fileUploadFailed(formName, file, uncompressedFile, compressionConfig)).pipe(
        tap(() => {
            SentryService.captureMessage('Error in file upload', {
                extra: {
                    error,
                },
            });

            FileUploadSubject.next({file, progress: -1});
        }),
    );
};

const persistFileObservable = (file, formName, uploadedFile, config) => {
    if (file.type.match('image') && !config.uploadImagesAsFile) {
        // Handle profile image uploads in a custom way
        if (config.type) {
            return Api.changeProfileImage({...config, image: uploadedFile});
        } else {
            return Api.completeImageUpload(uploadedFile, config.imageFor);
        }
    } else if (file.type.match('video')) {
        return Api.completeVideoUpload(uploadedFile);
    } else {
        return Api.completeFileUpload(uploadedFile);
    }
};

const persistFile = action$ =>
    action$.ofType(actionTypes.FILE_UPLOAD_FILE_UPLOAD_SUCCESS).mergeMap(({payload}) => {
        const {file, formName, uploadedFile, config} = payload;

        return defer(() => persistFileObservable(file, formName, uploadedFile, config)).pipe(
            retryWhen(
                genericRetryStrategy({
                    maxRetryAttempts: 3,
                    scalingDuration: 100,
                }),
            ),
            mergeMap(persistedFile => {
                const actionsArray = [];
                // Handle profile image uploads in a custom way
                if (config.type) {
                    actionsArray.push(Actions.clearFiles(formName));
                }

                actionsArray.push(
                    NewActions.filePersistSuccess.action(
                        formName,
                        file,
                        {...persistedFile, mediaType: Helpers.getFileMediaType(file), temporaryFileUrl: uploadedFile},
                        config,
                    ),
                );

                return actionsArray;
            }),
            catchError(() => of(Actions.filePersistFailed(formName, file))),
        );
    });

const cancelUpload = action$ =>
    action$
        .ofType(actionTypes.FILE_UPLOAD_REMOVE_FILE)
        // This is the nicest way we could think of to have a side effect that doesn't emit a resulting action
        .do(({payload}) => {
            const file = payload.file.file;
            const taggingAppModule = payload.taggingAppModule;
            if (file && file.xhr && file.xhr.abort && typeof file.xhr.abort === 'function') {
                file.xhr.abort();
            }

            if (payload.file.id && taggingAppModule) {
                Helpers.removeMediaTags([payload.file], taggingAppModule);
            }
        })
        .filter(() => false);

// Generic file upload observable
export const uploadFileObservable = (file, config) =>
    Api.uploadObservable(file).mergeMap(url => persistFileObservable(file, undefined, url, config));

export default combineEpics(uploadFile, persistFile, cancelUpload);
